import React, { useEffect, useState } from 'react';
import { 
  Layout, 
  Form, 
  Input, 
  Button, 
  Select, 
  Row, 
  Col, 
  message, 
  Space, 
  Divider, 
  Breadcrumb, 
  Typography, 
  Card,
  Upload,
  Avatar,
  Switch,
  Tag,
  Tooltip,
  Alert
} from 'antd';
import { 
  MinusCircleOutlined, 
  PlusOutlined, 
  UploadOutlined,
  SaveOutlined,
  BankOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { createOrg, updateOrg, getOrg } from '../../api';
import { storage, ref, uploadBytes, getDownloadURL } from '../../firebase';

const usStates = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia',
  'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland',
  'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const { Content } = Layout;
const { Option } = Select;
const { Title, Text } = Typography;

const CreateOrgScreen = ({ organizationData }) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [coversheetFields, setCoversheetFields] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [haveOrg, setHaveOrg] = useState(false);
  const [localOrgData, setLocalOrgData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  useEffect(() => {
    console.log('Organization Data from props:', organizationData);
    
    const fetchOrgData = async () => {
      try {
        const response = await getOrg();
        console.log('API Response:', response);
        const data = response.data;
        console.log('Organization Data from API:', data);
        
        setLocalOrgData(data);
        setHaveOrg(true);
        setLogoPreview(data.logoUrl);
        form.setFieldsValue({
          title: data.title,
          type: data.type,
          phone: data.phone,
          email: data.email,
          website: data.website,
          street: data.address?.street,
          state: data.address?.state,
          zipCode: data.address?.zipCode,
        });

        if (data.coversheetFields && data.coversheetFields.length > 0) {
          setCoversheetFields(data.coversheetFields);
        }
      } catch (error) {
        console.error('Error fetching organization data:', error);
        setHaveOrg(false);
      }
    };

    if (!organizationData) {
      console.log('No organization data from props, fetching from API');
      fetchOrgData();
    } else {
      console.log('Using organization data from props');
      setLocalOrgData(organizationData);
      setHaveOrg(true);
      setLogoPreview(organizationData.logoUrl);
      form.setFieldsValue({
        title: organizationData.title,
        type: organizationData.type,
        phone: organizationData.phone,
        email: organizationData.email,
        website: organizationData.website,
        street: organizationData.address?.street,
        state: organizationData.address?.state,
        zipCode: organizationData.address?.zipCode,
      });

      if (organizationData.coversheetFields && organizationData.coversheetFields.length > 0) {
        setCoversheetFields(organizationData.coversheetFields);
      }
    }
  }, [organizationData, form]);

  const handleUpload = async (file, organizationId) => {
    try {
      const storageRef = ref(storage, `public/${organizationId}/logo.jpg`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      return url;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw new Error('Failed to upload logo');
    }
  };

  const handleDetailSubmit = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      console.log('Form values:', values);
      let website = values.website;

      if (website && !/^https?:\/\//i.test(website)) {
        website = `https://${website}`;
      }

      let data = {
        title: values.title,
        type: values.type,
        phone: values.phone,
        email: values.email,
        website,
        address: {
          street: values.street,
          state: values.state,
          zipCode: values.zipCode,
          country: 'US',
        },
        coversheetFields,
      };

      // Remove undefined fields
      Object.keys(data).forEach(key => {
        if (data[key] === undefined) {
          delete data[key];
        }
      });
      
      // Remove undefined nested address fields
      if (data.address) {
        Object.keys(data.address).forEach(key => {
          if (data.address[key] === undefined) {
            delete data.address[key];
          }
        });
      }

      console.log('Update payload:', data);
      console.log('Have org:', haveOrg);
      console.log('Local org data:', localOrgData);

      let response;
      if (haveOrg && localOrgData?.id) {
        console.log('Updating organization with ID:', localOrgData.id);
        response = await updateOrg(data);
      } else {
        console.log('Creating new organization');
        response = await createOrg(data);
      }

      const newOrganizationId = response.data.id;
      console.log('Organization ID:', newOrganizationId);

      if (logoFile) {
        console.log('Uploading logo file');
        try {
          const logoUrl = await handleUpload(logoFile, newOrganizationId);
          console.log('Logo URL:', logoUrl);
          await updateOrg({ logoUrl });
        } catch (error) {
          console.error('Logo upload error:', error);
          message.error('Failed to upload logo. Please try again.');
          return;
        }
      }

      message.success('Organization saved successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Submit error:', error);
      message.error(error?.response?.data?.error || 'Failed to save organization.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (file) => {
    console.log('File selected:', file);
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      message.error('Please upload an image file');
      return false;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('File size should be less than 5MB');
      return false;
    }
    
    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      console.log('Setting preview:', e.target.result);
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    message.success('Logo selected successfully!');
    return false; // Prevent default upload
  };

  const addCoversheetField = () => {
    setCoversheetFields([...coversheetFields, { dataType: 'text', title: '', required: false, options: [] }]);
  };

  const updateCoversheetField = (index, field, value) => {
    const updatedFields = [...coversheetFields];
    updatedFields[index][field] = value;
    setCoversheetFields(updatedFields);
  };

  const removeCoversheetField = (index) => {
    const updatedFields = coversheetFields.filter((_, i) => i !== index);
    setCoversheetFields(updatedFields);
  };

  const dataTypeOptions = [
    { value: 'text', label: 'Text', icon: '📝' },
    { value: 'number', label: 'Number', icon: '🔢' },
    { value: 'email', label: 'Email', icon: '📧' },
    { value: 'date', label: 'Date', icon: '📅' },
    { value: 'url', label: 'URL', icon: '🔗' },
    { value: 'multiple-choice', label: 'Multiple Choice', icon: '☑️' },
    { value: 'single-choice', label: 'Single Choice', icon: '🔘' },
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Breadcrumb style={{ marginBottom: '16px' }}>
            <Breadcrumb.Item>
              <HomeOutlined />
            </Breadcrumb.Item>
            <Breadcrumb.Item>Dashboard</Breadcrumb.Item>
            <Breadcrumb.Item>
              <SettingOutlined />
              Organization Settings
            </Breadcrumb.Item>
          </Breadcrumb>
          <Title level={2} style={{ margin: 0, color: '#006971' }}>
            <BankOutlined style={{ marginRight: '12px' }} />
            Organization Settings
          </Title>
          <Text type="secondary" style={{ fontSize: '16px' }}>
            Manage your organization profile and application requirements
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleDetailSubmit}>
          <Row gutter={[24, 24]}>
            {/* Basic Information Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <InfoCircleOutlined />
                    Basic Information
                  </Space>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="title"
                      label="Organization Name"
                      rules={[{ required: true, message: 'Please enter the organization name' }]}
                    >
                      <Input 
                        size="large"
                        prefix={<BankOutlined />}
                        placeholder="Enter organization name" 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="type"
                      label="Organization Type"
                      rules={[{ required: true, message: 'Please enter the organization type' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="e.g., Hospital, Clinic, Medical Center" 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[{ required: true, message: 'Please enter the phone number' }]}
                    >
                      <Input 
                        size="large"
                        prefix={<PhoneOutlined />}
                        placeholder="(555) 123-4567" 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[{ required: true, message: 'Please enter a valid email', type: 'email' }]}
                    >
                      <Input 
                        size="large"
                        prefix={<MailOutlined />}
                        placeholder="contact@organization.com" 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  name="website"
                  label="Website"
                  rules={[{ required: true, message: 'Please enter the website URL' }]}
                >
                  <Input 
                    size="large"
                    prefix={<GlobalOutlined />}
                    placeholder="www.organization.com" 
                    style={{ borderRadius: '8px' }}
                  />
                </Form.Item>
              </Card>
            </Col>

            {/* Address & Logo Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <HomeOutlined />
                    Address & Branding
                  </Space>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={16}>
                    <Form.Item
                      name="street"
                      label="Street Address"
                      rules={[{ required: true, message: 'Please enter the street address' }]}
                    >
                      <Input 
                        size="large"
                        placeholder="123 Main Street" 
                        style={{ borderRadius: '8px' }}
                      />
                    </Form.Item>
                    
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={16}>
                        <Form.Item
                          name="state"
                          label="State"
                          rules={[{ required: true, message: 'Please select the state' }]}
                        >
                          <Select 
                            size="large"
                            placeholder="Select state"
                            style={{ borderRadius: '8px' }}
                            showSearch
                            filterOption={(input, option) =>
                              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {usStates.map((state) => (
                              <Option key={state} value={state}>{state}</Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          name="zipCode"
                          label="ZIP Code"
                          rules={[{ required: true, message: 'Please enter the ZIP code' }]}
                        >
                          <Input 
                            size="large"
                            placeholder="12345" 
                            style={{ borderRadius: '8px' }}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                  
                  <Col xs={24} md={8}>
                    <Form.Item label="Organization Logo">
                      <div style={{ textAlign: 'center' }}>
                        <Avatar
                          size={120}
                          src={logoPreview}
                          icon={<BankOutlined />}
                          style={{ 
                            backgroundColor: '#006971', 
                            marginBottom: '16px',
                            border: '4px solid #f0f0f0'
                          }}
                        />
                        <div>
                          <Upload
                            beforeUpload={handleFileChange}
                            accept="image/*"
                            showUploadList={false}
                          >
                            <Button 
                              icon={<UploadOutlined />}
                              style={{ borderRadius: '8px' }}
                            >
                              {logoPreview ? 'Change Logo' : 'Upload Logo'}
                            </Button>
                          </Upload>
                          <div style={{ marginTop: '8px' }}>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                              Max 5MB, JPG/PNG
                            </Text>
                          </div>
                        </div>
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            {/* Coversheet Fields Card */}
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <FileTextOutlined />
                    Application Form Fields
                    <Tooltip title="Define custom fields for job applications">
                      <InfoCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </Space>
                }
                extra={
                  <Button 
                    type="dashed" 
                    onClick={addCoversheetField}
                    icon={<PlusOutlined />}
                    style={{ borderRadius: '8px' }}
                  >
                    Add Field
                  </Button>
                }
                style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
              >
                {coversheetFields.length === 0 && (
                  <Alert
                    message="No custom fields added yet"
                    description="Add custom fields to collect additional information from job applicants."
                    type="info"
                    showIcon
                    style={{ marginBottom: '16px', borderRadius: '8px' }}
                  />
                )}

                {coversheetFields.map((field, index) => (
                  <Card 
                    key={index} 
                    size="small"
                    style={{ 
                      marginBottom: '16px',
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px'
                    }}
                    title={
                      <Space>
                        <Tag color="blue">Field {index + 1}</Tag>
                        {field.title && <Text strong>{field.title}</Text>}
                      </Space>
                    }
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => removeCoversheetField(index)}
                        size="small"
                      >
                        Remove
                      </Button>
                    }
                  >
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="Field Title"
                          required
                          style={{ marginBottom: '16px' }}
                        >
                          <Input
                            value={field.title}
                            onChange={(e) => updateCoversheetField(index, 'title', e.target.value)}
                            placeholder="Enter field title"
                            style={{ borderRadius: '6px' }}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="Data Type"
                          required
                          style={{ marginBottom: '16px' }}
                        >
                          <Select
                            value={field.dataType}
                            onChange={(value) => updateCoversheetField(index, 'dataType', value)}
                            style={{ borderRadius: '6px' }}
                          >
                            {dataTypeOptions.map((option) => (
                              <Option key={option.value} value={option.value}>
                                <span style={{ marginRight: '8px' }}>{option.icon}</span>
                                {option.label}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item
                          label="Required Field"
                          required
                          style={{ marginBottom: '16px' }}
                        >
                          <div style={{ paddingTop: '4px' }}>
                            <Switch
                              checked={field.required}
                              onChange={(checked) => updateCoversheetField(index, 'required', checked)}
                              checkedChildren="Yes"
                              unCheckedChildren="No"
                            />
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                    {(field.dataType === 'multiple-choice' || field.dataType === 'single-choice') && (
                      <Form.Item
                        label="Options"
                        required
                        style={{ marginBottom: '0' }}
                      >
                        <Input.TextArea
                          placeholder="Enter options separated by commas (e.g., Option 1, Option 2, Option 3)"
                          value={field.options.join(', ')}
                          onChange={(e) => updateCoversheetField(index, 'options', e.target.value.split(',').map(option => option.trim()))}
                          rows={2}
                          style={{ borderRadius: '6px' }}
                        />
                      </Form.Item>
                    )}
                  </Card>
                ))}
              </Card>
            </Col>
          </Row>

          {/* Save Button */}
          <div style={{ 
            marginTop: '32px', 
            textAlign: 'center',
            padding: '24px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large"
              loading={loading}
              icon={<SaveOutlined />}
              style={{ 
                height: '50px',
                padding: '0 40px',
                borderRadius: '25px',
                fontSize: '16px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #006971 0%, #004d54 100%)',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0, 105, 113, 0.3)'
              }}
            >
              {haveOrg ? 'Update Organization' : 'Save Organization'}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default CreateOrgScreen;
